import React from 'react';
import { Button, Input, Typography } from 'antd';
import { LoginOutlined, MailOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { getFontClass } from '../util';
import LanguageToggle from './languageToggle';

interface ResetPasswordFormProps {
  forgotemail: string;
  setForgotEmail: (value: string) => void;
  error: string | null;
  apiLoading: boolean;
  onReset: () => void;
  onBack: () => void;
  t: (key: string) => string;
}

export const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
  forgotemail,
  setForgotEmail,
  error,
  apiLoading,
  onReset,
  onBack,
  t,
}) => {
  const textColor = 'text-white';
  const labelColor = 'text-white/80';
  const iconColor = 'text-white/60';
  const buttonClass = '!bg-transparent border-2 border-white text-white hover:!bg-white/10';
  
  return (
    <form onSubmit={(e) => { e.preventDefault(); onReset(); }} className="w-full">
      {/* Header with Logo and Language Toggle */}
      <div className="flex justify-between items-center mb-6 sm:mb-8">
        <Typography className={getFontClass(`${textColor} text-3xl sm:text-4xl drop-shadow-lg`, 700)}>
          NeoPaas
        </Typography>
        <div className="flex gap-2">
          <LanguageToggle />
        </div>
      </div>

      {/* Title */}
      <Typography className={getFontClass(`text-xl sm:text-2xl mb-6 sm:mb-8 ${textColor} font-light`, 500)}>
        {t('resetyour')}
      </Typography>

      {/* Email Input */}
      <div className="mb-5 sm:mb-6">
        <label htmlFor="Email" className={getFontClass(`block mb-2 ${labelColor} text-sm`, 400)}>
          {t('email')}
        </label>
        <Input
          prefix={<MailOutlined className={iconColor} />}
          type="text"
          id="email"
          size="large"
          placeholder={t('emailPlaceholder')}
          className="glass-input w-full rounded-xl min-h-[44px]"
          value={forgotemail}
          onChange={(e) => setForgotEmail(e.target.value)}
        />
      </div>

      {/* Error Message */}
      <div className="min-h-[2rem] mb-4">
        {error && (
          <Typography className={getFontClass('text-red-400 text-center text-xs sm:text-sm bg-red-500/10 py-2 px-3 rounded-lg', 400)}>
            {t(error)}
          </Typography>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col gap-3 sm:gap-4 mb-5 sm:mb-6">
        <Button
          className={`${buttonClass} w-full min-h-[44px] sm:h-12 rounded-xl font-semibold transition-all duration-300 text-sm sm:text-base`}
          size="large"
          iconPosition="end"
          onClick={onReset}
          icon={<LoginOutlined />}
          loading={apiLoading}
        >
          {t('reset')}
        </Button>

        <Button
          size="large"
          className={`${buttonClass} w-full min-h-[44px] sm:h-12 rounded-xl transition-all duration-300 text-sm sm:text-base`}
          iconPosition="start"
          icon={<ArrowLeftOutlined />}
          onClick={onBack}
        >
          {t('back')}
        </Button>
      </div>

      {/* Version Code */}
      <Typography className={getFontClass('text-xs text-white/40 text-center mt-4', 400)}>
        {t('versionCode')}: {__COMMIT_HASH__}
      </Typography>
    </form>
  );
};
